import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/ExerciseLayout.css';

import QuestionLearnButton from '../full_mock_test/attempt/QuestionLearnButton';

const MoversListenPart4 = ({ exerciseData, attemptId, exerciseId, showHint, isReviewMode, fillHintSignal }) => {
  const [answers, setAnswers] = useState({});
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (attemptId && exerciseId) {
      // 1. Prioritize API data in review mode
      if (isReviewMode && exerciseData.questions) {
        const migratedAnswers = {};
        exerciseData.questions.forEach(q => {
          if (q.user_answer && typeof q.user_answer.answer === 'string') {
            migratedAnswers[q.id] = q.user_answer.answer;
          }
        });
        if (Object.keys(migratedAnswers).length > 0) {
          setAnswers(migratedAnswers);
          setIsHydrated(true);
          return;
        }
      }

      // 2. Otherwise load from localStorage
      const attemptKey = `attempt_${attemptId}`;
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) {
        try {
          const allAnswers = JSON.parse(savedAttempt);
          const savedData = allAnswers[exerciseId];
          if (savedData && typeof savedData === 'object') {
            const migratedAnswers = {};
            Object.keys(savedData).forEach(qId => {
              const value = savedData[qId];
              migratedAnswers[qId] = (typeof value === 'object' && value !== null && 'answer' in value) ? value.answer : value;
            });
            setAnswers(migratedAnswers);
          }
        } catch (e) {
          console.error("Failed to parse saved attempt from localStorage", e);
        }
      }
      setIsHydrated(true);
    }
  }, [attemptId, exerciseId, isReviewMode, exerciseData.questions]);

  const saveAnswersToLocalStorage = useCallback((currentAnswers) => {
    if (!isHydrated || !attemptId || !exerciseId) return;

    const attemptKey = `attempt_${attemptId}`;
    let allAnswers = {};
    try {
      const savedAttempt = localStorage.getItem(attemptKey);
      if (savedAttempt) allAnswers = JSON.parse(savedAttempt) || {};
    } catch (e) {
      allAnswers = {};
    }

    const answersToSave = {};
    Object.keys(currentAnswers).forEach((qId) => {
      answersToSave[qId] = { answer: currentAnswers[qId] };
    });

    allAnswers[exerciseId] = answersToSave;
    localStorage.setItem(attemptKey, JSON.stringify(allAnswers));
  }, [attemptId, exerciseId, isHydrated]);


  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    const debounceSave = setTimeout(() => {
      saveAnswersToLocalStorage(answers);
    }, 500);

    return () => {
      clearTimeout(debounceSave);
      saveAnswersToLocalStorage(answers);
    };
  }, [answers, isHydrated, saveAnswersToLocalStorage]);

  useEffect(() => {
    if (fillHintSignal > 0) {
      const newAnswers = {};
      exerciseData.questions.forEach(q => {
        if (q.accepted_answers && q.accepted_answers.length > 0) {
          newAnswers[q.id] = q.accepted_answers[0];
        }
      });
      setAnswers(newAnswers);
      saveAnswersToLocalStorage(newAnswers);
    }
  }, [fillHintSignal, exerciseData.questions, saveAnswersToLocalStorage]);

  if (!exerciseData || !exerciseData.content_data || !exerciseData.questions) {
    return <div>Loading exercise data...</div>;
  }

  const sortedQuestions = [...exerciseData.questions].sort((a, b) => a.order_index - b.order_index);
  const exampleQuestion = sortedQuestions.find(q => q.points === 0);
  const mainQuestions = sortedQuestions.filter(q => q.points > 0);

  const ASSET_BASE_URL = process.env.REACT_APP_MEDIA_PREFIX || 'http://localhost:8000';

  const handleOptionSelect = (questionId, optionId) => {
    setAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionId]: optionId,
    }));
  };

  const QuestionBlock = ({ question, questionNumber, showHint, isReviewMode, isHydrated }) => {
    const isExample = question.points === 0;
    const selectedOption = isExample ? (question.accepted_answers && question.accepted_answers[0]) || '' : answers[question.id];

    // Fix: is_correct is at top level
    const isCorrect = isReviewMode && (question?.is_correct === true || question?.is_correct === 'true');
    // Treat null/undefined as incorrect if in review mode
    const isIncorrect = isReviewMode && !isCorrect;
    const userSelectedAnswerId = question?.user_answer?.answer;
    const correctAnswerId = question.accepted_answers[0];

    return (
      <div className={`bg-white dark:bg-slate-800/50 rounded-3xl border-2 transition-all p-3 md:p-6 relative flex flex-col gap-4 ${isExample ? 'border-amber-200 bg-amber-50/10' : 'border-slate-100 dark:border-slate-700 hover:border-slate-200 shadow-sm'}`}>
        {/* Row 1: Question text */}
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-1">
            {isExample
              ? <span className="font-black text-white bg-amber-500 px-2 py-1 rounded-lg text-[10px] uppercase tracking-wider shadow-sm">e.g.</span>
              : <span className="font-black text-slate-400 bg-slate-50 dark:bg-slate-900 px-3 py-1.5 rounded-xl text-sm border border-slate-100 dark:border-slate-800 shadow-sm">{questionNumber}.</span>
            }
          </div>
          <p className="font-bold text-slate-800 dark:text-white text-base md:text-lg leading-snug flex-1">
            {question.question_text.replace(/\(Example\)/gi, '').trim()}
          </p>
          {isReviewMode && !isExample && <QuestionLearnButton question={question} />}
        </div>

        {/* Row 2: 3 image options */}
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          {question.options.map(option => {
            const isThisOptionCorrect = option.id === correctAnswerId;
            const isThisOptionUserSelected = option.id === userSelectedAnswerId;
            const isSelected = selectedOption === option.id;
            const isHintCorrect = showHint && isThisOptionCorrect;

            let cardClasses = `flex flex-col items-center gap-1.5 p-2 bg-white dark:bg-slate-800 rounded-2xl shadow-sm border-2 transition-all cursor-pointer relative ${isSelected ? 'border-amber-400 bg-amber-50/80 dark:bg-amber-900/40' : 'border-slate-100 dark:border-slate-700 hover:border-gray-200 hover:shadow-md'}`;
            if (isHintCorrect) {
              cardClasses = 'flex flex-col items-center gap-1.5 p-2 rounded-2xl shadow-sm border-2 cursor-pointer relative border-amber-500 bg-amber-50 dark:bg-amber-900/20';
            } else if (isReviewMode) {
              if (isThisOptionCorrect) {
                cardClasses = 'flex flex-col items-center gap-1.5 p-2 rounded-2xl shadow-sm border-2 cursor-pointer relative border-green-500 bg-green-50/20 dark:bg-green-900/20';
              } else if (isThisOptionUserSelected && !isCorrect) {
                cardClasses = 'flex flex-col items-center gap-1.5 p-2 rounded-2xl shadow-sm border-2 cursor-pointer relative border-red-500 bg-red-50/20 dark:bg-red-900/20';
              }
            }

            return (
              <div
                key={option.id}
                className={cardClasses}
                onClick={() => !isExample && !showHint && !isReviewMode && isHydrated && handleOptionSelect(question.id, option.id)}
              >
                <div className="w-full h-24 md:h-32 flex justify-center items-center mb-1 bg-slate-50 dark:bg-slate-800/80 rounded-xl p-1.5 border border-slate-100 dark:border-slate-700 shadow-inner overflow-visible">
                  <img src={`${ASSET_BASE_URL}${option.image_url}`} alt={option.explain} className="max-h-full max-w-full object-contain mix-blend-multiply dark:mix-blend-normal" />
                </div>
                <div className="flex items-center gap-2 w-full justify-center">
                  <span className={`font-display font-black text-sm ${isSelected || isHintCorrect || (isReviewMode && isThisOptionCorrect) ? 'text-amber-600' : 'text-slate-400'}`}>{option.id}</span>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-all
                    ${isHintCorrect ? 'bg-amber-500 border-amber-500'
                      : isReviewMode
                        ? (isThisOptionCorrect ? 'bg-green-500 border-green-500' : (isThisOptionUserSelected && !isCorrect ? 'bg-red-500 border-red-500' : 'border-slate-300 dark:border-slate-600'))
                        : (isSelected ? 'bg-amber-500 border-amber-500' : 'border-slate-300 dark:border-slate-600')
                    }`}>
                    {(isHintCorrect || (isReviewMode && isThisOptionCorrect) || (!showHint && !isReviewMode && isSelected)) &&
                      <span className="text-white text-[10px] font-bold material-symbols-outlined leading-none">check</span>
                    }
                    {isReviewMode && isThisOptionUserSelected && !isCorrect &&
                      <span className="text-white text-[10px] font-bold material-symbols-outlined leading-none">close</span>
                    }
                  </div>
                </div>
                {isReviewMode && isThisOptionCorrect && <div className="absolute -top-1.5 -right-1.5 bg-white rounded-full leading-none shadow-sm border border-green-100"><span className="correct-checkmark !w-5 !h-5 !text-[10px] leading-none">✓</span></div>}
                {isReviewMode && isIncorrect && isThisOptionUserSelected && !isThisOptionCorrect && <div className="absolute -top-1.5 -right-1.5 bg-white rounded-full leading-none shadow-sm border border-red-100"><span className="incorrect-checkmark !w-5 !h-5 !text-[10px] leading-none">✗</span></div>}
              </div>
            )
          })}
        </div>
      </div>
    );
  };

  const allQuestions = exampleQuestion ? [exampleQuestion, ...mainQuestions] : mainQuestions;

  return (
    <div className="w-full h-full p-1 md:px-10">
      <div className="relative flex flex-col h-full bg-transparent dark:from-slate-800/50 dark:to-card-dark rounded-2xl p-1 md:p-4 overflow-y-auto custom-scrollbar">
        {/* Using m-auto here ensures vertical centering IF the content is shorter than the viewport, but prevents clipping top items if it's taller, unlike justify-center on the flex parent */}
        <div className="w-full m-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-4 h-full">
            {allQuestions.map((q, index) => (
              <QuestionBlock
                key={q.id}
                question={q}
                questionNumber={q.points === 0 ? 0 : index}
                showHint={showHint}
                isReviewMode={isReviewMode}
                isHydrated={isHydrated}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MoversListenPart4;